import type { KeyboardEvent } from "react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Command, CommandInput, CommandList } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getAutoDirection } from "@/lib/direction";

import { GlobalSearchChatsSection } from "./GlobalSearchChatsSection";
import { GlobalSearchFooter } from "./GlobalSearchFooter";
import { GlobalSearchGroupList } from "./GlobalSearchGroupList";
import { GlobalSearchHeader } from "./GlobalSearchHeader";
import { GlobalSearchServerStatus } from "./GlobalSearchServerStatus";
import { GlobalSearchTabMessage } from "./GlobalSearchTabMessage";
import { GlobalSearchTabs } from "./GlobalSearchTabs";
import { useGlobalSearchFocusGuard } from "./hooks/use-global-search-focus-guard";
import { useGlobalSearchHighlight } from "./hooks/use-global-search-highlight";
import { useGlobalSearchResults } from "./hooks/use-global-search-results";
import { useGlobalSearchSelect } from "./hooks/use-global-search-select";
import { useGlobalSearchTabKeys } from "./hooks/use-global-search-tab-keys";
import { useGlobalSearchTabs } from "./hooks/use-global-search-tabs";
import type { IGlobalSearchContentProps } from "./types";

// NOTE: NSAI-6589 — the palette body. Mounted only while the dialog is open,
// so every query behind it (organizations, FAQ, /search/global) runs only then.
// Filtering is off because every source ranks its own results.
// NSAI-6853 — a category tab strip sits between the box and the results; the
// active tab narrows them and ←/→ (at the edges of the query) switch it.
const GlobalSearchContentComponent = ({
  onClose,
}: IGlobalSearchContentProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const [query, setQuery] = useState("");
  const { tabs, activeTab, onSelectTab, onSelectAdjacentTab } =
    useGlobalSearchTabs();
  // NOTE: NSAI-6750 — a picked result clears the query before closing. The
  // body normally unmounts on close, but its exit animation keeps it mounted
  // until `animationend`, and a result that opens a new tab (the docs action)
  // hides this one mid-animation — reopening then reused the stale query.
  const handleClose = useCallback((): void => {
    setQuery("");
    onClose();
  }, [onClose]);
  const {
    leadingGroups,
    trailingGroups,
    chats,
    server,
    isSearching,
    hasOtherResults,
    isChatsVisible,
    isServerStatusVisible,
    tabStatus,
  } = useGlobalSearchResults({ query, tab: activeTab, onClose: handleClose });
  const handleSelectItem = useGlobalSearchSelect({ onClose: handleClose });
  const { rootRef, value, onValueChange, onKeyDown, onPointerMoveCapture } =
    useGlobalSearchHighlight({
      query,
      tab: activeTab,
      leadingGroups,
      trailingGroups,
      chats: chats.chats,
      semanticHits: chats.semanticHits,
    });
  useGlobalSearchFocusGuard({ rootRef });
  const handleTabKeys = useGlobalSearchTabKeys({ onSelectAdjacentTab });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      handleTabKeys(event);
      onKeyDown(event);
    },
    [handleTabKeys, onKeyDown],
  );

  return (
    <Command
      ref={rootRef}
      shouldFilter={false}
      value={value}
      onValueChange={onValueChange}
      onKeyDown={handleKeyDown}
      onPointerMoveCapture={onPointerMoveCapture}
      className="rounded-none bg-transparent text-foreground"
    >
      <GlobalSearchHeader
        title={t("title")}
        description={t("description")}
        closeLabel={t("close_label")}
      >
        <CommandInput
          autoFocus
          dir={getAutoDirection({ isDisabled: !query })}
          inputMode="text"
          value={query}
          onValueChange={setQuery}
          placeholder={t("placeholder")}
          aria-label={t("trigger_label")}
          wrapperClassName="flex items-center border-0 px-2"
          className="flex h-12 w-full bg-transparent py-3 text-lg outline-none placeholder:text-foreground-secondary/80 disabled:cursor-not-allowed disabled:opacity-50"
          iconClassName="me-3 h-5 w-5 shrink-0 text-foreground-secondary"
          data-testid="search-chats-input"
        />
      </GlobalSearchHeader>
      <GlobalSearchTabs
        tabs={tabs}
        activeTab={activeTab}
        onSelectTab={onSelectTab}
        onSelectAdjacentTab={onSelectAdjacentTab}
      />
      <div className="mx-4 h-px bg-glass-edge sm:mx-5" />
      <CommandList className="max-h-none" data-testid="search-chats-list">
        {/* NOTE: NSAI-6763 — Radix's `display: table` content wrapper grows
            to its widest row, which pushed the "…", the badges and RTL titles
            past the dialog edge. Constrained, every row is as wide as the list. */}
        <ScrollArea
          isViewportContentConstrained
          className="px-2 py-2 sm:px-3"
          viewportClassName="h-[min(26rem,60vh)] max-h-[min(26rem,60vh)]"
          data-testid="search-chats-scroll"
          viewportRef={chats.setScrollContainer}
        >
          <GlobalSearchGroupList
            groups={leadingGroups}
            onSelectItem={handleSelectItem}
          />
          {isChatsVisible && (
            <GlobalSearchChatsSection
              state={chats}
              isSearching={isSearching}
              hasOtherResults={hasOtherResults}
            />
          )}
          <GlobalSearchGroupList
            groups={trailingGroups}
            onSelectItem={handleSelectItem}
          />
          <GlobalSearchTabMessage
            status={tabStatus}
            tab={activeTab}
            query={query}
          />
          {isServerStatusVisible && (
            <GlobalSearchServerStatus
              isLoading={server.isLoading}
              isError={server.isError}
              onRetry={server.onRetry}
            />
          )}
        </ScrollArea>
      </CommandList>
      <GlobalSearchFooter />
    </Command>
  );
};

export const GlobalSearchContent = memo(GlobalSearchContentComponent);
